import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Bold, Italic, List, ListOrdered, Underline, RemoveFormatting, Smile, Type, Link, Indent, Outdent } from 'lucide-react';

const EMOJI_CATEGORIES = [
  { label: 'Common', emojis: ['😀','😊','👍','🎉','✅','❌','⭐','🔥','💯','👏','🙏','💪','📚','🎓','📝','✏️','📅','⏰','🏆','🎯'] },
  { label: 'Faces', emojis: ['😃','😄','😁','😆','🥹','😅','🤣','😂','🙂','😉','😊','😇','🥰','😍','🤩','😘','😗','😋','🤔','🤗'] },
  { label: 'Hands', emojis: ['👋','🤚','✋','🖖','👌','🤌','✌️','🤞','🫰','🤟','🤘','🤙','👈','👉','👆','👇','☝️','👍','👎','✊'] },
  { label: 'Objects', emojis: ['📖','📕','📗','📘','📙','📓','📔','📒','📚','📝','✏️','📌','📎','🔑','💡','🎒','💼','📊','📈','📉'] },
  { label: 'Symbols', emojis: ['❤️','🧡','💛','💚','💙','💜','🤎','🖤','🤍','💔','❣️','💕','💞','💓','💗','💖','💝','✨','🌟','💫'] },
];

const FONT_SIZES = [
  { label: 'Small', value: '2' },
  { label: 'Normal', value: '3' },
  { label: 'Medium', value: '4' },
  { label: 'Large', value: '5' },
  { label: 'X-Large', value: '6' },
];

const sanitizeHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const allowedTags = ['P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'A', 'FONT', 'SPAN'];
  const clean = (node) => {
    const fragment = document.createDocumentFragment();
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) {
        fragment.appendChild(document.createTextNode(child.textContent));
      } else if (child.nodeType === 1) {
        const tag = child.tagName;
        if (allowedTags.includes(tag)) {
          const el = document.createElement(tag);
          if (tag === 'A' && child.getAttribute('href')) el.setAttribute('href', child.getAttribute('href'));
          if (tag === 'FONT' && child.getAttribute('size')) el.setAttribute('size', child.getAttribute('size'));
          el.appendChild(clean(child));
          fragment.appendChild(el);
        } else if (['H1','H2','H3','H4','H5','H6','DIV','SECTION','ARTICLE'].includes(tag)) {
          const p = document.createElement('P');
          p.appendChild(clean(child));
          fragment.appendChild(p);
        } else {
          fragment.appendChild(clean(child));
        }
      }
    }
    return fragment;
  };
  const result = document.createElement('div');
  result.appendChild(clean(div));
  return result.innerHTML;
};

export const RichTextEditor = ({ value, onChange, placeholder, minHeight = '200px', maxHeight = '400px' }) => {
  const editorRef = useRef(null);
  const isUserTyping = useRef(false);
  const lastPropValue = useRef(value);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState(0);
  const [showFontSize, setShowFontSize] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const emojiRef = useRef(null);
  const fontRef = useRef(null);
  const linkRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== lastPropValue.current && !isUserTyping.current) {
      editorRef.current.innerHTML = value || '';
      lastPropValue.current = value;
    }
  }, [value]);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmojiPicker(false);
      if (fontRef.current && !fontRef.current.contains(e.target)) setShowFontSize(false);
      if (linkRef.current && !linkRef.current.contains(e.target)) setShowLinkInput(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const notifyChange = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      lastPropValue.current = html;
      onChange(html);
    }
  }, [onChange]);

  // Save and restore selection for toolbar actions
  const savedSelection = useRef(null);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedSelection.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (savedSelection.current && editorRef.current) {
      editorRef.current.focus();
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelection.current);
    }
  };

  const execCmd = (cmd, val = null) => {
    restoreSelection();
    document.execCommand(cmd, false, val);
    notifyChange();
    saveSelection();
  };

  const handleInput = () => {
    isUserTyping.current = true;
    notifyChange();
    saveSelection();
    setTimeout(() => { isUserTyping.current = false; }, 100);
  };

  // Save selection on mouse up and key up
  const handleSelect = () => { saveSelection(); };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      document.execCommand('insertHTML', false, sanitizeHtml(html));
    } else {
      const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const withBreaks = escaped.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>');
      document.execCommand('insertHTML', false, `<p>${withBreaks}</p>`);
    }
    notifyChange();
  };

  const clearFormatting = () => {
    restoreSelection();
    document.execCommand('removeFormat', false, null);
    notifyChange();
    saveSelection();
  };

  const insertEmoji = (emoji) => {
    restoreSelection();
    document.execCommand('insertText', false, emoji);
    notifyChange();
    saveSelection();
    setShowEmojiPicker(false);
  };

  const changeFontSize = (size) => {
    restoreSelection();
    document.execCommand('fontSize', false, size);
    notifyChange();
    saveSelection();
    setShowFontSize(false);
  };

  const openLinkInput = () => {
    setShowEmojiPicker(false);
    setShowFontSize(false);
    // Check if there's already a link at cursor
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const anchor = sel.anchorNode?.parentElement?.closest('a');
      setLinkUrl(anchor ? anchor.href : 'https://');
    } else {
      setLinkUrl('https://');
    }
    setShowLinkInput(true);
  };

  const insertLink = () => {
    const url = linkUrl.trim();
    if (!url || url === 'https://') {
      // Remove link
      restoreSelection();
      document.execCommand('unlink', false, null);
    } else {
      restoreSelection();
      const sel = window.getSelection();
      if (sel && sel.toString().length === 0) {
        // No text selected — insert the URL as linked text
        document.execCommand('insertHTML', false, `<a href="${url}" target="_blank" style="color:#2563eb;text-decoration:underline;">${url}</a>`);
      } else {
        document.execCommand('createLink', false, url);
        // Style the new link
        const newLink = sel.anchorNode?.parentElement?.closest('a');
        if (newLink) {
          newLink.setAttribute('target', '_blank');
          newLink.style.color = '#2563eb';
          newLink.style.textDecoration = 'underline';
        }
      }
    }
    notifyChange();
    saveSelection();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const removeLink = () => {
    restoreSelection();
    document.execCommand('unlink', false, null);
    notifyChange();
    saveSelection();
    setShowLinkInput(false);
    setLinkUrl('');
  };

  const preventBlur = (e) => e.preventDefault();

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-1.5 bg-slate-50 border-b border-slate-200 flex-wrap">
        <button type="button" onMouseDown={preventBlur} onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onMouseDown={preventBlur} onClick={() => execCmd('italic')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Italic">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" onMouseDown={preventBlur} onClick={() => execCmd('underline')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Underline">
          <Underline className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={preventBlur} onClick={() => execCmd('insertUnorderedList')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Bullet List">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onMouseDown={preventBlur} onClick={() => execCmd('insertOrderedList')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Numbered List">
          <ListOrdered className="w-4 h-4" />
        </button>
        <button type="button" onMouseDown={preventBlur} onClick={() => execCmd('indent')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Indent">
          <Indent className="w-4 h-4" />
        </button>
        <button type="button" onMouseDown={preventBlur} onClick={() => execCmd('outdent')} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Outdent">
          <Outdent className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-slate-300 mx-1" />

        {/* Font Size Dropdown */}
        <div className="relative" ref={fontRef}>
          <button
            type="button"
            onMouseDown={preventBlur}
            onClick={() => { setShowFontSize(!showFontSize); setShowEmojiPicker(false); }}
            className={`p-1.5 rounded hover:bg-slate-200 text-slate-600 ${showFontSize ? 'bg-slate-200' : ''}`}
            title="Font Size"
          >
            <Type className="w-4 h-4" />
          </button>
          {showFontSize && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1 w-28">
              {FONT_SIZES.map((fs) => (
                <button
                  key={fs.value}
                  type="button"
                  onMouseDown={preventBlur}
                  onClick={() => changeFontSize(fs.value)}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 hover:text-blue-700 text-slate-700"
                >
                  {fs.label}
                </button>
              ))}
            </div>
          )}
        </div>


        {/* Link */}
        <div className="relative" ref={linkRef}>
          <button
            type="button"
            onMouseDown={preventBlur}
            onClick={openLinkInput}
            className={`p-1.5 rounded hover:bg-slate-200 text-slate-600 ${showLinkInput ? 'bg-slate-200' : ''}`}
            title="Insert Link"
          >
            <Link className="w-4 h-4" />
          </button>
          {showLinkInput && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-3 w-72" onMouseDown={preventBlur}>
              <label className="text-xs font-medium text-slate-600 mb-1 block">URL</label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); insertLink(); } }}
                placeholder="https://example.com"
                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 mb-2"
                autoFocus
              />
              <div className="flex gap-2">
                <button type="button" onClick={insertLink} className="flex-1 px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700">
                  Apply
                </button>
                <button type="button" onClick={removeLink} className="px-2 py-1 text-xs font-medium text-red-600 border border-red-200 rounded hover:bg-red-50">
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>


        {/* Emoji Picker */}
        <div className="relative" ref={emojiRef}>
          <button
            type="button"
            onMouseDown={preventBlur}
            onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowFontSize(false); }}
            className={`p-1.5 rounded hover:bg-slate-200 text-slate-600 ${showEmojiPicker ? 'bg-slate-200' : ''}`}
            title="Insert Emoji"
          >
            <Smile className="w-4 h-4" />
          </button>
          {showEmojiPicker && (
            <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 w-72" onMouseDown={preventBlur}>
              <div className="flex border-b border-slate-100 px-1 pt-1 gap-0.5 overflow-x-auto">
                {EMOJI_CATEGORIES.map((cat, idx) => (
                  <button
                    key={cat.label}
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={() => setEmojiCategory(idx)}
                    className={`px-2 py-1 text-xs rounded-t whitespace-nowrap ${idx === emojiCategory ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-10 gap-0.5 p-2 max-h-36 overflow-y-auto">
                {EMOJI_CATEGORIES[emojiCategory].emojis.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onMouseDown={preventBlur}
                    onClick={() => insertEmoji(emoji)}
                    className="w-7 h-7 flex items-center justify-center text-lg rounded hover:bg-slate-100 cursor-pointer"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-slate-300 mx-1" />
        <button type="button" onMouseDown={preventBlur} onClick={clearFormatting} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Clear Formatting">
          <RemoveFormatting className="w-4 h-4" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        onMouseUp={handleSelect}
        onKeyUp={handleSelect}
        className="px-4 py-3 text-sm text-slate-800 focus:outline-none overflow-y-auto [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:pl-2 [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:pl-2 [&_li]:mb-1 [&_a]:text-blue-600 [&_a]:underline"
        style={{ fontFamily: 'Figtree, sans-serif', lineHeight: '1.7', minHeight, maxHeight }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};
